import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { searchServiceRequestsGeneralUtil } from 'src/pages/ServiceRequests/helpers/filterServiceRequests';

export type ProjectFilter = string | 'all';

/* BE models visibility as `isExternallyVisible` (external = visible to
 * homeowners); our UI speaks "internal only", the inverse. Absent field is
 * treated as external, matching the BE default. */
export const getOriginalIsInternalOnly = (sr: IServiceRequest): boolean => {
  return sr.isExternallyVisible === false;
};

export const indexServiceRequestsById = (
  serviceRequests: IServiceRequest[]
): Map<string, IServiceRequest> => {
  const byId: Map<string, IServiceRequest> = new Map();

  for (const sr of serviceRequests) {
    byId.set(sr.id, sr);
  }

  return byId;
};

export const serviceRequestMatchesProjectFilter = (
  sr: IServiceRequest,
  projectFilter: ProjectFilter
): boolean => {
  if (projectFilter === 'all') {
    return true;
  }

  return sr.propertyId === projectFilter;
};

export const filterServiceRequests = (
  serviceRequests: IServiceRequest[],
  projectFilter: ProjectFilter,
  searchQuery: string
): IServiceRequest[] => {
  const searched: IServiceRequest[] = searchServiceRequestsGeneralUtil(
    serviceRequests,
    searchQuery
  );

  const matches: IServiceRequest[] = [];

  for (const sr of searched) {
    if (!serviceRequestMatchesProjectFilter(sr, projectFilter)) {
      continue;
    }

    matches.push(sr);
  }

  return matches;
};

export const toggleIdInSelection = (
  previous: Set<string>,
  id: string
): Set<string> => {
  const next: Set<string> = new Set(previous);

  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }

  return next;
};

export const setIdsSelected = (
  previous: Set<string>,
  ids: string[],
  selected: boolean
): Set<string> => {
  const next: Set<string> = new Set(previous);

  for (const id of ids) {
    if (selected) {
      next.add(id);
    } else {
      next.delete(id);
    }
  }

  return next;
};

export const collectStagedServiceRequests = (
  selectedIds: Set<string>,
  serviceRequestsById: Map<string, IServiceRequest>
): IServiceRequest[] => {
  const staged: IServiceRequest[] = [];

  for (const id of selectedIds) {
    const sr: IServiceRequest | undefined = serviceRequestsById.get(id);

    if (!sr) {
      continue;
    }

    staged.push(sr);
  }

  return staged;
};

interface RecordWithId {
  id: string;
}

/* The changed rows for the confirmation screen: the staged SRs we sent,
 * narrowed to the ids the server reports as actually updated (the response
 * is the source of truth for partial updates). We keep the full staged
 * objects for display rather than the id-only response records. */
export const selectConfirmedChanges = (
  updatedRecords: readonly RecordWithId[],
  sentServiceRequests: IServiceRequest[]
): IServiceRequest[] => {
  const changedIds: Set<string> = new Set(
    updatedRecords.map((record: RecordWithId) => record.id)
  );

  return sentServiceRequests.filter((sr: IServiceRequest) =>
    changedIds.has(sr.id)
  );
};

export const filterActualChanges = (
  staged: IServiceRequest[],
  target: boolean
): IServiceRequest[] => {
  return staged.filter(
    (sr: IServiceRequest) => getOriginalIsInternalOnly(sr) !== target
  );
};

export const filterUnchanged = (
  staged: IServiceRequest[],
  target: boolean
): IServiceRequest[] => {
  return staged.filter(
    (sr: IServiceRequest) => getOriginalIsInternalOnly(sr) === target
  );
};